/**
 * The modal shell and form primitives the Inventar detail dialogs share —
 * Inventar 2.0, S7a.
 *
 * Presentation only. Every element below exists in the page today; this file
 * gives the four assignment dialogs one description of their chrome instead of
 * four hand-copied ones, and moves that chrome onto the theme tokens S6b put
 * on the upper workspace.
 *
 * TWO THINGS ARE LOAD-BEARING AND MUST NOT BE "IMPROVED":
 *
 *  1. The <h4> stays a DIRECT child of the panel that also holds the form and
 *     the buttons. The S6a suites resolve a dialog as
 *     `getByRole("heading", { name }).parentElement`, so a header band around
 *     the title would re-point that at a box with no controls in it.
 *  2. `DialogField` renders its label text as a direct child of the <label>,
 *     with no wrapper element. E24 matches „Menge (frei: 6)" against the
 *     node's full textContent; a <span> around the text would make both the
 *     span and the label match and turn a getBy into a multiple-match error.
 *
 * No role="dialog", no aria-modal, no focus trap, no Escape handler: none of
 * those exist today, and adding them is behaviour, not presentation. Tracked
 * as a follow-up, not smuggled into a redesign slice.
 */
import React from "react";

/**
 * Centred modal panel. Scrolls inside itself so a tall dialog still fits a
 * short viewport. `maxWidthClassName` exists for the one dialog that has always
 * been wider — Artikel bearbeiten carries eleven controls — and defaults to the
 * width every other dialog already used.
 */
export function InventoryDialog({ title, children, maxWidthClassName = "max-w-md" }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div
        className={`max-h-[88vh] w-full ${maxWidthClassName} overflow-y-auto rounded-[10px] border border-border bg-card p-5 text-foreground shadow-xl sm:p-6`}
      >
        <h4 className="m-0 text-[17px] font-semibold leading-tight text-foreground">{title}</h4>
        {children}
      </div>
    </div>
  );
}

/** The muted explainer line some dialogs carry under their title. */
export function DialogHint({ children }) {
  return <p className="m-0 mt-2 text-[12px] leading-snug text-muted-foreground">{children}</p>;
}

/** The dialog's error slot. */
export function DialogError({ children }) {
  return <p className="m-0 mt-2 text-[13px] font-medium text-destructive">{children}</p>;
}

/**
 * A labelled control. The <label> wraps its control, which is how the page
 * associates the two today — implicit labelling, no id/htmlFor bookkeeping.
 */
export function DialogField({ label, children, className = "" }) {
  return (
    <label
      className={`block text-[10px] font-medium uppercase tracking-[0.09em] text-muted-foreground ${className}`}
    >
      {label}
      {children}
    </label>
  );
}

/** Footer action row. */
export function DialogActions({ children }) {
  return <div className="mt-4 flex flex-wrap items-center justify-end gap-2">{children}</div>;
}

export const DIALOG_CONTROL =
  "mt-1 w-full rounded-[8px] border border-border bg-muted px-3 py-2 text-[13px] font-medium normal-case tracking-normal text-foreground outline-none transition-colors focus:border-ring focus:ring-2 focus:ring-ring";

export const DIALOG_BTN_SECONDARY =
  "rounded-[8px] border border-border bg-muted px-4 py-2 text-[12.5px] font-semibold text-foreground transition-colors hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

export const DIALOG_BTN_PRIMARY =
  "rounded-[8px] bg-primary px-4 py-2 text-[12.5px] font-semibold text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50";

export const DIALOG_BTN_DESTRUCTIVE =
  "rounded-[8px] bg-destructive px-4 py-2 text-[12.5px] font-semibold text-destructive-foreground transition-colors hover:bg-destructive/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50";
